import React, { useEffect, useState } from 'react'
import { Notice } from '../sso-workspace/fleet-page'
import { formatCount, plural } from '../sso-workspace/format'
import { getAuditHealth } from './audit-api'
import { useAuditCapabilities } from './use-audit-capabilities'

// The audit trail's own health (content GET /audit/health, SYS_AUDIT_LOG):
// { since, items: [{ key, count, severity }] }, listing only the problems.
// One plain sentence per problem; a key this admin doesn't know yet still
// shows, by name.
const SENTENCES = {
  trigger_errors: n => `${plural(n, 'audit write error')} in the last 24 hours.`,
  unattributed_changes: n => `${plural(n, 'change')} recorded without who made ${n === 1 ? 'it' : 'them'}.`,
  default_partition_rows: n => `${plural(n, 'entry', 'entries')} stored outside ${n === 1 ? 'its' : 'their'} month or day; storage needs attention.`,
  missing_partitions: () => 'Storage for the coming days or months is missing; new entries may land in the wrong place.',
  retention_failures: n => `The clean-up of old entries failed ${plural(n, 'time')}.`,
  retention_paused: () => 'Deleting old entries is paused on this server.',
  retention_settings: () => 'A retention setting is missing or out of bounds, so nothing is being deleted.',
  recording_off: () => 'Recording is switched off.',
  requests_dropped: n => `${plural(n, 'request')} ${n === 1 ? "wasn't" : "weren't"} recorded because the log was overloaded.`,
  redaction_failed: n => `${plural(n, 'request')} ${n === 1 ? 'was' : 'were'} stored without their bodies because secrets couldn't be removed safely.`,
  geo_unavailable: () => "Locations aren't available: this server has no IP location database installed."
}

export const healthSentence = ({ key, count }) => {
  const say = SENTENCES[key]
  return say ? say(Number(count) || 0) : `Something needs attention: ${key} (${formatCount(count)}).`
}

// Super admins only: for anyone else it reads nothing and renders nothing.
// Read once on mount; a failed read shows nothing (the pages work without
// it). omit: keys the page already explains itself (the Request log's own
// location notice).
export const HealthBanner = ({ omit }) => {
  const caps = useAuditCapabilities()
  const allowed = Boolean(caps.loaded && caps.sysAuditLog)
  const [items, setItems] = useState([])

  useEffect(() => {
    if (!allowed) return undefined
    let live = true
    getAuditHealth().then(
      h => live && setItems((h && Array.isArray(h.items) && h.items) || []),
      () => live && setItems([])
    )
    return () => {
      live = false
    }
  }, [allowed])

  const shown = allowed ? items.filter(i => !(omit || []).includes(i.key)) : []
  if (!shown.length) return null

  return (
    <Notice tone="warn" role="status">
      <strong>The audit trail needs attention.</strong>
      <ul className="au-health">
        {shown.map(i => <li key={i.key}>{healthSentence(i)}</li>)}
      </ul>
    </Notice>
  )
}
